import { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Routes } from '../routes/routes';

/**
 * Hook detecting if a component is visible and firing an event if a hidden component is to be rendered.
 *
 * @export
 * @template T type of component to observe
 * @param {React.MutableRefObject<T>} ref ref of component to observe
 * @param {() => void} [notifyIntersectionChange] event fired when visibel state of component changes
 * @param {string} [rootMargin='0px'] intersection buffer for accuracy
 * @returns
 */
export function useOnScreen<T extends Element>(
    ref: React.RefObject<T>,
    notifyIntersectionChange?: () => void,
    rootMargin = '0px'
) {
    // State and setter for storing whether element is visible
    const [isIntersecting, setIsIntersecting] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                // Update our state when observer callback fires
                setIsIntersecting(entry.isIntersecting);
                if (notifyIntersectionChange) {
                    notifyIntersectionChange();
                }
            },
            {
                rootMargin,
            }
        );
        if (ref.current) {
            const obServedRef = ref.current;
            observer.observe(obServedRef);
            return () => {
                observer.unobserve(obServedRef);
            };
        }
        return undefined;
    }, [notifyIntersectionChange, ref, rootMargin]); // Empty array ensures that effect is only run on mount and unmount

    return { isIntersecting };
}

/**
 * Used to calculate the rest height of a page. With this you can set a component to fill the rest height of a page.
 * Remarks: only works for components direktly rendering. If your component is rendered lazy, like components on non-active tabs you need useOnScreen too.
 * @export
 * @template T type of the container around the element to set the restHeight
 * @param {React.RefObject<T>} ref ref of the container sourounding the element
 * @param {React.Dispatch<React.SetStateAction<number>>} setRestHeight state setter for the hieght of the lement to fill the rest of the page
 * @returns
 */
export function useDimensions<T extends Element>(
    ref: React.RefObject<T>,
    setRestHeight: React.Dispatch<React.SetStateAction<number>>
) {
    const updateRestHeight = useCallback(() => {
        if (ref.current) {
            setRestHeight(window.innerHeight - ref.current.getBoundingClientRect().top);
        }
    }, [ref, setRestHeight]);

    useLayoutEffect(() => {
        window.addEventListener('resize', updateRestHeight);
        updateRestHeight();
        return () => {
            window.removeEventListener('resize', updateRestHeight);
        };
    }, [ref, updateRestHeight]);

    return { updateRestHeight };
}

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
        width,
        height,
    };
}
export function useWindowDimensions() {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowDimensions;
}

export function useNavigation() {
    const navigate = useNavigate();

    return async (route: Routes, queryParams?: Record<string, string | number>, hash?: string) => {
        let result = `/${route}`;
        if (hash) {
            result += `#${hash}`;
        }
        if (queryParams) {
            const searchParams = new URLSearchParams(
                Object.entries(queryParams).map(([key, value]) => [key, String(value)])
            );
            result += `?${searchParams.toString()}`;
        }
        await navigate(result);
    };
}

export const useDocumentTitle = (title: string) => {
    const [documentTitle, setDocumentTitle] = useState(title);

    useEffect(() => {
        document.title = documentTitle;
    }, [documentTitle]);

    return { documentTitle, setDocumentTitle };
};

export const useQrCodeDialog = () => {
    const [open, setOpen] = useState(false);
    const [qrCodeValue, setQrCodeValue] = useState<string | undefined>(undefined);

    const handleClose = () => {
        setOpen(false);
        setQrCodeValue(undefined);
    };

    const handleOpen = (value?: string) => {
        setQrCodeValue(value);
        setOpen(true);
    };

    return {
        qrCodeDialogOpen: open,
        qrCodeValue,
        handleCloseQrCodeDialog: handleClose,
        handleOpenQrCodeDialog: handleOpen,
    };
};
